import { useState, useEffect, useRef, useCallback } from "react";

const API = `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const hdr = () => ({ Authorization: `Bearer ${token()}` });

/**
 * ProductSearch — Ortak ürün arama komponenti
 * Kullanım:
 *   <ProductSearch
 *     value={form.product_code}
 *     onChange={(code, name) => { set("product_code", code); set("product_name", name); }}
 *     t={t}
 *   />
 */
export default function ProductSearch({ value = "", onChange, placeholder = "Ürün kodu ara...", t, disabled = false, showName = true }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setQuery(prev => prev === (value || "") ? prev : (value || ""));
  }, [value]);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/product-search?q=${encodeURIComponent(q)}`, { headers: hdr() });
      if (res.ok) {
        const list = await res.json();
        setResults(list);
        setOpen(list.length > 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setSelectedName("");
    if (onChange) onChange(v, "");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 220);
  };

  const handleSelect = (p) => {
    const code = p.product_code || "";
    const name = p.product_name || "";
    setQuery(code);
    setSelectedName(name);
    setOpen(false);
    setResults([]);
    if (onChange) onChange(code, name);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
    if (e.key === "Enter" && results.length > 0) handleSelect(results[0]);
  };

  const highlight = (text, q) => {
    if (!q || !text) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: t?.accent?.blue || "#60a5fa", fontWeight: 800 }}>{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const inp = {
    width: "100%", padding: "9px 36px 9px 12px",
    background: t?.bg?.input || "#1a1a2e",
    border: `1px solid ${open ? (t?.accent?.blue || "#60a5fa") : (t?.border?.input || "#333")}`,
    borderRadius: open ? "8px 8px 0 0" : 8,
    color: t?.text?.primary || "#fff",
    fontSize: 13, outline: "none", boxSizing: "border-box",
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "text",
    transition: "border-color 0.15s",
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <style>{`@keyframes ps-spin { to { transform: translateY(-50%) rotate(360deg) } }`}</style>

      <div style={{ position: "relative" }}>
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) setOpen(true); else if (query.length >= 1) search(query); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          style={inp}
          autoComplete="off"
        />
        {/* Sağ ikon: loading veya temizle */}
        {loading ? (
          <div style={{ position: "absolute", right: 10, top: "50%", width: 14, height: 14, border: `2px solid ${t?.border?.default || "#333"}`, borderTopColor: t?.accent?.blue || "#60a5fa", borderRadius: "50%", animation: "ps-spin 0.7s linear infinite", transform: "translateY(-50%)" }} />
        ) : query ? (
          <button onClick={() => { setQuery(""); setSelectedName(""); setResults([]); setOpen(false); if (onChange) onChange("", ""); }}
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t?.text?.muted || "#888", cursor: "pointer", fontSize: 15, padding: 2, lineHeight: 1 }}>✕</button>
        ) : (
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: t?.text?.muted || "#888", pointerEvents: "none" }}>🔍</span>
        )}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
          background: t?.bg?.modal || "#1e1e2e",
          border: `1px solid ${t?.accent?.blue || "#60a5fa"}`,
          borderTop: "none", borderRadius: "0 0 10px 10px",
          maxHeight: 280, overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        }}>
          {results.map((p, i) => (
            <div key={i} onClick={() => handleSelect(p)}
              style={{
                padding: "9px 14px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 10,
                borderBottom: i < results.length - 1 ? `1px solid ${t?.border?.subtle || "#2a2a3e"}` : "none",
              }}
              onMouseEnter={e => e.currentTarget.style.background = t?.bg?.rowHover || "rgba(96,165,250,0.1)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: `${t?.accent?.blue || "#60a5fa"}18`, border: `1px solid ${t?.accent?.blue || "#60a5fa"}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>📦</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t?.text?.primary || "#fff" }}>{highlight(p.product_code, query)}</div>
                {p.product_name && <div style={{ fontSize: 11, color: t?.text?.muted || "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.product_name}</div>}
              </div>
              {p.category && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, background: t?.bg?.tag || "#2a2a3e", color: t?.text?.muted || "#888", flexShrink: 0 }}>{p.category}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Seçilen ürün adı */}
      {showName && selectedName && (
        <div style={{ marginTop: 4, fontSize: 11, color: t?.accent?.green || "#22c55e", fontWeight: 600 }}>✓ {selectedName}</div>
      )}
    </div>
  );
}
